// src/components/shared/BottomSheet.vue
<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAppStore } from '@/stores/app'
import { useMapStore } from '@/stores/map'
import { eventBus } from '@/utils/eventBus'
import { smoothScroll } from '@/utils/scroll'

const appStore = useAppStore()
const mapStore = useMapStore()
const currentHeight = ref(window.innerHeight * 0.6)
const startY = ref(0)
const isDragging = ref(false)
const bottomSheet = ref(null)
const wasMoved = ref(false)

// Define snap points
const BottomSheetStates = {
	get FULLSCREEN() {
		return window.innerHeight - getSafeAreaInsetTop()
	},
	get LARGE() {
		return window.innerHeight * 0.8
	},
	get NORMAL() {
		return window.innerHeight * 0.6
	},
	get SMALL() {
		return window.innerHeight * 0.23
	},
	get HIDDEN() {
		return 0
	},
	get AUTOFIT() {
		return -1
	},
}

// Compute current state based on height
const currentState = computed(() => {
	const height = currentHeight.value
	const threshold = 10 // Tolerance for height comparison
	for (const [state, stateHeight] of Object.entries(BottomSheetStates)) {
		if (Math.abs(height - stateHeight) <= threshold) {
			return state.toLowerCase()
		}
	}
	return ''
})

const handleStart = (event) => {
	wasMoved.value = false
	if (event.type === 'touchstart') {
		startY.value = event.touches[0].clientY
	} else {
		startY.value = event.clientY
		isDragging.value = true
		document.addEventListener('mousemove', handleMove)
		document.addEventListener('mouseup', handleEndGlobal)
	}
	// Remove transition during drag
	bottomSheet.value.style.transition = ''
}

const handleMove = (event) => {
	wasMoved.value = true
	if ((event.type === 'mousemove' && isDragging.value) || event.type === 'touchmove') {
		const currentY = event.type === 'mousemove' ? event.clientY : event.touches[0].clientY
		const diff = startY.value - currentY
		currentHeight.value = Math.min(BottomSheetStates.FULLSCREEN, Math.max(BottomSheetStates.SMALL, currentHeight.value + diff))
		startY.value = currentY
	}
}

const handleEndGlobal = (event) => {
	handleEnd(event)
	document.removeEventListener('mousemove', handleMove)
	document.removeEventListener('mouseup', handleEndGlobal)
}

const handleEnd = (event) => {
	if (event.type === 'mouseup' || event.type === 'touchend') {
		isDragging.value = false

		// Calculate the closest snap point
		const snapPoints = Object.values(BottomSheetStates)
		const closestSnap = snapPoints.reduce((prev, curr) => {
			return Math.abs(curr - currentHeight.value) < Math.abs(prev - currentHeight.value) ? curr : prev
		})

		// If the current height is already the closest snap point, do nothing
		if (currentHeight.value == closestSnap || !wasMoved.value) {
			return
		}

		// Calculate the difference between current height and closest snap
		const diff = Math.abs(currentHeight.value - closestSnap)
		var mapUpdateDelay = diff > 50 ? 300 : 0

		// Set the closest snap point as the new height
		currentHeight.value = closestSnap

		// Standard animation duration
		var animationDuration = '0.3s'

		// Use animation duration from event if specified
		if (event.duration > 0) {
			mapUpdateDelay = event.duration
			animationDuration = event.duration / 1000 + 's'
		}

		// Add transition for the snap animation
		bottomSheet.value.style.transition = 'height ' + animationDuration + ' ease-out'

		// Update the map view when the user dragged the bottom sheet
		if (wasMoved.value) updateMap(mapUpdateDelay)
	}
}

const updateMap = (delay = 300) => {
	// Update the mapview when height animation is done
	setTimeout(() => {
		// Call map update function here when we implement it
		mapStore.showCurrentTrips(true)
	}, delay)
}

const setHeight = (height, duration, source, refreshMap = true, callback) => {
	const deltaHeight = Math.abs(height - currentHeight.value)
	// Lets make the animation faster if we are moving a small distance
	if (duration && deltaHeight < 100) {
		duration = duration / 2
		if (deltaHeight < 20) duration = duration / 2
	}
	console.warn('BottomSheet: setHeight', height, 'adjusting', deltaHeight, duration + 'ms', source)
	const el = bottomSheet.value
	if (duration) {
		const done = (e) => e.propertyName === 'height' && (el.removeEventListener('transitionend', done), callback?.())
		el.addEventListener('transitionend', done)
		el.style.transition = `height ${duration}ms ease-out`
	} else {
		callback?.()
	}
	currentHeight.value = height
	if (refreshMap) updateMap()
}

const autoResize = (duration = 300, autoScroll = true, allowFullscreen = false, source = '', refreshMap = true, callback) => {
	console.warn('BottomSheet: autoResize', source)
	var innerBodyElement = document.querySelector('.page.active .body .inner')
	var bodyElement = document.querySelector('.page.active .body')
	var bottomSheetElement = document.querySelector('.bottom-sheet')
	if (bodyElement && bottomSheetElement) {
		var headerElement = document.querySelector('.page.active header')
		var footerElement = document.querySelector('.page.active footer')
		var sheetHeightExcludingBody = headerElement && footerElement ? headerElement.clientHeight + footerElement.clientHeight : bottomSheetElement.clientHeight - bodyElement.clientHeight
		var bodyHeight = bodyElement.scrollHeight
		// If body height is more than the inner body element, we can reduce the height
		if (bodyHeight > innerBodyElement.clientHeight) {
			bodyHeight = innerBodyElement.clientHeight
		}
		var calculatedHeight = bodyHeight + sheetHeightExcludingBody
		if (calculatedHeight < BottomSheetStates.NORMAL) calculatedHeight = BottomSheetStates.NORMAL
		if (calculatedHeight > BottomSheetStates.LARGE && !allowFullscreen) calculatedHeight = BottomSheetStates.LARGE
		if (calculatedHeight > BottomSheetStates.FULLSCREEN) calculatedHeight = BottomSheetStates.FULLSCREEN
		calculatedHeight = Math.round(calculatedHeight)
		console.log('bottomSheetElement.clientHeight', bottomSheetElement.clientHeight, 'innerBodyElement.clientHeight', innerBodyElement.clientHeight, 'bodyElement.clientHeight', bodyElement.clientHeight, 'sheetHeightExcludingBody', sheetHeightExcludingBody, 'newHeight', calculatedHeight)
		setTimeout(() => {
			//const hasVerticalScroll = bodyElement.scrollHeight > bodyElement.clientHeight
			//if (hasVerticalScroll) {
			setHeight(calculatedHeight, duration, source, refreshMap, callback)
			if (autoScroll) smoothScroll('.page.active .body', 'bottom', 200)
			//}
		}, 300)
	}
}

const close = (duration = 500, source = '', refreshMap, callback) => {
	setHeight(BottomSheetStates.HIDDEN, duration, source, refreshMap, callback)
}

const open = (duration = 500, source = '', refreshMap, callback) => {
	setHeight(BottomSheetStates.NORMAL, duration, source, refreshMap, callback)
}

onMounted(() => {
	eventBus.on('sheet-height', ({ height, duration }) => {
		setHeight(height, duration)
	})
})

function getSafeAreaInsetTop() {
	const insetTop = getComputedStyle(document.documentElement).getPropertyValue('--sat')
	return parseInt(insetTop) || 0
}

defineExpose({
	autoResize,
	open,
	close,
	setHeight,
	setHeightHidden: (duration, source, refreshMap, callback) => setHeight(BottomSheetStates.HIDDEN, duration, source, refreshMap, callback),
	setHeightSmall: (duration, source, refreshMap, callback) => setHeight(BottomSheetStates.SMALL, duration, source, refreshMap, callback),
	setHeightNormal: (duration, source, refreshMap, callback) => setHeight(BottomSheetStates.NORMAL, duration, source, refreshMap, callback),
	setHeightLarge: (duration, source, refreshMap, callback) => setHeight(BottomSheetStates.LARGE, duration, source, refreshMap, callback),
	setHeightFullscreen: (duration, source, refreshMap, callback) => setHeight(BottomSheetStates.FULLSCREEN, duration, source, refreshMap, callback),
	handleStart,
	handleMove,
	handleEnd,
	currentHeight,
	currentState,
	BottomSheetStates,
})
</script>

<template>
	<div ref="bottomSheet" id="bottom" class="bottom-sheet" :style="{ height: `${currentHeight}px` }" :class="[{ 'footer-only': appStore.editStep == 'splitTripActive' }, currentState]">
		<div class="top">
			<slot name="top"></slot>
		</div>
		<div class="drag-handle" @touchstart.passive="handleStart" @touchmove.passive="handleMove" @touchend.passive="handleEnd" @mousedown.passive="handleStart" @mouseleave.passive="handleEnd"></div>
		<div class="pages">
			<slot></slot>
		</div>
	</div>
</template>

<style>
.bottom-sheet {
	display: flex;
	flex-direction: column;
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: var(--sheet-background-color, #faf8f7);
	border-top-left-radius: 1rem;
	border-top-right-radius: 1rem;
	z-index: 999;
	color: black;
	box-shadow: 0 5px 40px rgba(0, 0, 0, 0.3);
	transform: translateY(110vh);
}

.bottom-sheet.fullscreen,
.bottom-sheet.fullscreen .page.active header {
	border-top-left-radius: 0px;
	border-top-right-radius: 0px;
}

.bottom-sheet.footer-only {
	max-height: none;
	height: fit-content !important;
}

.bottom-sheet.footer-only .page.active {
	justify-content: end;
}

.bottom-sheet.footer-only .body,
.bottom-sheet.footer-only .header,
.bottom-sheet.footer-only header,
.bottom-sheet.footer-only .top,
.bottom-sheet.footer-only .drag-handle {
	display: none;
}

.loaded .bottom-sheet {
	animation: slide-up 0.5s ease-out 1s forwards;
}

@keyframes slide-up {
	to {
		transform: translateY(0%);
	}
}

.drag-handle {
	height: 60px;
	justify-content: center;
	align-items: center;
	cursor: row-resize;
	width: 100%;
	top: -40px;
	position: absolute;
	z-index: 9999;
	display: flex;
}

.drag-handle::after {
	content: ' ';
	position: absolute;
	left: 0;
	right: 0;
	bottom: 6px;
	margin-left: auto;
	margin-right: auto;
	border-radius: 4px;
	width: 15vw;
	height: 5px;
	background-color: rgba(0, 0, 0, 0.15);
}
</style>
